import { Button, Card, Form, Input, Select, Space, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Row = {
  id: string;
  workflow_no: string;
  id_kind: string;
  status: string;
  external_value?: string | null;
  admission_no?: string;
  first_name?: string;
  last_name?: string;
};

export default function IdsWorkflowListPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.ids.manage') || user?.permissions?.includes('education.admin')
  );
  const [status, setStatus] = useState<string | undefined>();
  const [idKind, setIdKind] = useState<string | undefined>();
  const [q, setQ] = useState('');

  const list = useQuery({
    queryKey: ['education-id-workflows', status, idKind, q],
    queryFn: async () => {
      const res = await api.get<Row[]>('/education/ids/workflows', {
        params: { status, id_kind: idKind, q: q || undefined },
      });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  return (
    <EducationPageChrome
      title={t('idWorkflows')}
      description={t('idWorkflowsIntro')}
      extra={
        <Space>
          <Button onClick={() => navigate('/education/ids')}>{t('ids')}</Button>
          {canManage ? (
            <Button type="primary" onClick={() => navigate('/education/ids/workflows/new')}>
              {t('newIdWorkflow')}
            </Button>
          ) : null}
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <Form layout="inline" onFinish={() => list.refetch()}>
            <Form.Item label={t('idKind')}>
              <Select
                allowClear
                style={{ minWidth: 120 }}
                value={idKind}
                onChange={(v) => setIdKind(v || undefined)}
                options={[
                  { value: 'pen', label: 'PEN' },
                  { value: 'apaar', label: 'APAAR' },
                ]}
              />
            </Form.Item>
            <Form.Item label={t('status')}>
              <Select
                allowClear
                style={{ minWidth: 150 }}
                value={status}
                onChange={(v) => setStatus(v || undefined)}
                options={[
                  { value: 'draft', label: t('statusDraft') },
                  { value: 'consent_pending', label: t('statusConsentPending') },
                  { value: 'consented', label: t('statusConsented') },
                  { value: 'submitted', label: t('statusSubmitted') },
                  { value: 'issued', label: t('statusIssued') },
                  { value: 'verified', label: t('statusVerified') },
                  { value: 'withdrawn', label: t('statusWithdrawn') },
                  { value: 'cancelled', label: t('statusCancelled') },
                ]}
              />
            </Form.Item>
            <Form.Item label={t('search')}>
              <Input value={q} onChange={(e) => setQ(e.target.value)} allowClear style={{ width: 160 }} />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={list.isFetching}>
              {t('filter')}
            </Button>
          </Form>
        </Card>
        <Card size="small">
          <Table
            rowKey="id"
            size="small"
            loading={list.isLoading}
            dataSource={list.data ?? []}
            pagination={{ pageSize: 25 }}
            onRow={(row) => ({
              onClick: () => navigate(`/education/ids/workflows/${row.id}`),
              style: { cursor: 'pointer' },
            })}
            columns={[
              { title: t('workflowNo'), dataIndex: 'workflow_no', width: 160 },
              {
                title: t('idKind'),
                dataIndex: 'id_kind',
                width: 90,
                render: (v: string) => v.toUpperCase(),
              },
              {
                title: t('student'),
                render: (_: unknown, row: Row) =>
                  `${row.admission_no || ''} — ${[row.first_name, row.last_name].filter(Boolean).join(' ')}`,
              },
              {
                title: t('externalValue'),
                dataIndex: 'external_value',
                render: (v: string | null | undefined) => v || '—',
              },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 130,
                render: (v: string) => <Tag>{v}</Tag>,
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
